import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt   = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'
const money = (n) => n != null && n !== '' ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'

const STATUS = {
  active: { color: '#15803d', bg: '#f0fdf4', label: 'Active' },
  paused: { color: '#b45309', bg: '#fffbeb', label: 'Paused' },
  ended:  { color: '#6b7280', bg: '#f3f4f6', label: 'Ended'  },
}

// Mirrors the backend's validateAdImageDimensions (ads/ads.js) — same rule,
// checked client-side first so a bad image gets caught before filling out
// the rest of the form, not just on submit. Matches igihe.com's own
// leaderboard banner size exactly (728x90, ratio 8:1) rather than scaling
// up — keeps the ad a small strip, not a big block.
const AD_MIN_WIDTH        = 728
const AD_MIN_HEIGHT       = 90
const AD_ASPECT_RATIO     = 8      // width / height
const AD_ASPECT_TOLERANCE = 0.5    // accept 7.5–8.5

const validateAdImageDimensions = (width, height) => {
  if (width < AD_MIN_WIDTH || height < AD_MIN_HEIGHT) {
    return `Image must be at least ${AD_MIN_WIDTH}x${AD_MIN_HEIGHT}px (got ${width}x${height})`
  }
  const ratio = width / height
  if (Math.abs(ratio - AD_ASPECT_RATIO) > AD_ASPECT_TOLERANCE) {
    return `Image must have an ${AD_ASPECT_RATIO}:1 width:height ratio, e.g. 728x90 (got ${width}x${height})`
  }
  return null
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const inp = {
  width: '100%', height: 42, borderRadius: 8, border: '1px solid #e9e9e4',
  padding: '0 13px', fontSize: 13, fontWeight: 500, color: '#111317',
  outline: 'none', background: '#fff', fontFamily: "'Plus Jakarta Sans',sans-serif",
  boxSizing: 'border-box',
}
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 700, color: '#111317',
  textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6,
  fontFamily: "'JetBrains Mono',monospace",
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px', flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 7 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

// ── Create / Edit Modal ────────────────────────────────────────────────────────
function AdModal({ ad, onClose, onSaved }) {
  const isEdit = !!ad
  const fileRef = useRef(null)
  const [form, setForm] = useState({
    advertiser_name:    ad?.advertiser_name    || '',
    advertiser_contact: ad?.advertiser_contact || '',
    title:              ad?.title              || '',
    link_url:           ad?.link_url           || '',
    budget_amount:      ad?.budget_amount      || '',
    status:             ad?.status             || 'active',
    start_date:         ad?.start_date ? ad.start_date.slice(0, 10) : '',
    end_date:           ad?.end_date   ? ad.end_date.slice(0, 10)   : '',
  })
  const [imageFile, setImageFile] = useState(null)
  const [preview,   setPreview]   = useState(ad?.image_url || null)
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleFile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const dimError = validateAdImageDimensions(img.naturalWidth, img.naturalHeight)
      if (dimError) {
        setError(dimError)
        URL.revokeObjectURL(url)
        return
      }
      setError('')
      setImageFile(file)
      setPreview(url)
    }
    img.onerror = () => {
      setError('Could not read this image file.')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.advertiser_name.trim()) { setError('Advertiser name is required.'); return }
    if (!form.title.trim())           { setError('Title is required.'); return }
    if (!isEdit && !imageFile)        { setError('An ad image is required.'); return }

    setLoading(true); setError('')
    try {
      const fd = new FormData()
      fd.append('advertiser_name',    form.advertiser_name.trim())
      fd.append('advertiser_contact', form.advertiser_contact.trim())
      fd.append('title',              form.title.trim())
      fd.append('link_url',           form.link_url.trim())
      fd.append('budget_amount',      form.budget_amount || '')
      fd.append('status',             form.status)
      fd.append('start_date',         form.start_date)
      fd.append('end_date',           form.end_date)
      if (imageFile) fd.append('image', imageFile)

      const res = isEdit
        ? await api.put(`/ads/admin/${ad.ad_id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        : await api.post('/ads/admin/add', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      onSaved(res.data.ad)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save ad')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 520, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{isEdit ? 'Edit Ad' : 'New Ad'}</div>
            <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2 }}>Shown to viewers in the Browse feed</div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', lineHeight: 1 }}>×</div>
        </div>

        <form onSubmit={handleSave}>
          <div style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Image */}
            <div>
              <label style={lbl}>Ad Image {!isEdit && '*'}</label>
              <div onClick={() => fileRef.current?.click()}
                style={{ height: 140, borderRadius: 10, border: '1px dashed #e9e9e4', background: preview ? '#000' : '#f9f9f7', cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {preview
                  ? <img src={preview} alt="Ad preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : <span style={{ fontSize: 12, color: '#9a9a94' }}>Click to upload image</span>
                }
              </div>
              <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 5 }}>
                JPEG, PNG, WebP or GIF — min 728×90px, 8:1 width:height ratio (leaderboard banner), up to 5MB.
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
            </div>

            {/* Advertiser */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={lbl}>Advertiser Name *</label>
                <input required value={form.advertiser_name} onChange={e => set('advertiser_name', e.target.value)}
                  placeholder="Acme Inc." style={inp} />
              </div>
              <div>
                <label style={lbl}>Advertiser Contact</label>
                <input value={form.advertiser_contact} onChange={e => set('advertiser_contact', e.target.value)}
                  placeholder="email or phone" style={inp} />
              </div>
            </div>

            {/* Title */}
            <div>
              <label style={lbl}>Title *</label>
              <input required value={form.title} onChange={e => set('title', e.target.value)}
                placeholder="Ad headline / caption" style={inp} />
            </div>

            {/* Link + Budget */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={lbl}>Link URL</label>
                <input type="url" value={form.link_url} onChange={e => set('link_url', e.target.value)}
                  placeholder="https://…" style={inp} />
              </div>
              <div>
                <label style={lbl}>Budget Amount</label>
                <input type="number" min="0" step="0.01" value={form.budget_amount} onChange={e => set('budget_amount', e.target.value)}
                  placeholder="0.00" style={inp} />
              </div>
            </div>

            {/* Status + dates */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div>
                <label style={lbl}>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Start Date</label>
                <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} style={inp} />
              </div>
              <div>
                <label style={lbl}>End Date</label>
                <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} style={inp} />
              </div>
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '9px 13px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ {error}</div>
            )}
          </div>

          <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose}
              style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              Cancel
            </button>
            <button type="submit" disabled={loading}
              style={{ flex: 2, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Ad'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Ads() {
  const [ads,          setAds]          = useState([])
  const [loading,       setLoading]      = useState(true)
  const [error,         setError]        = useState('')
  const [statusFilter,  setStatusFilter] = useState('')
  const [search,        setSearch]       = useState('')
  const [page,          setPage]         = useState(1)
  const [totalPages,    setTotalPages]   = useState(1)
  const [totalCount,    setTotalCount]   = useState(0)
  const [stats,         setStats]        = useState({ total: 0, active: 0, paused: 0, total_impressions: 0 })
  const [modalAd,       setModalAd]      = useState(undefined) // undefined = closed, null = create, object = edit
  const [busyId,        setBusyId]       = useState(null)

  const PER_PAGE = 20

  const fetchAds = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        ...(statusFilter && { status: statusFilter }),
        ...(search        && { search }),
      })
      const res = await api.get(`/ads/admin/all?${params}`)
      setAds(res.data.ads || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total      || 0)
      setStats(res.data.stats || { total: 0, active: 0, paused: 0, total_impressions: 0 })
    } catch {
      setError('Failed to load ads')
      setAds([])
    } finally { setLoading(false) }
  }, [page, statusFilter, search])

  useEffect(() => { fetchAds() }, [fetchAds])
  useEffect(() => { setPage(1) }, [statusFilter, search])

  const handleSaved = () => fetchAds()

  const handleStatusChange = async (ad, status) => {
    setBusyId(ad.ad_id)
    try {
      await api.patch(`/ads/admin/${ad.ad_id}/status`, { status })
      setAds(prev => prev.map(a => a.ad_id === ad.ad_id ? { ...a, status } : a))
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status')
    } finally { setBusyId(null) }
  }

  const handleEnd = (ad) => {
    if (!window.confirm(`End "${ad.title}"? This ad will stop showing to viewers.`)) return
    handleStatusChange(ad, 'ended')
  }

  const handleDelete = async (ad) => {
    if (!window.confirm(`Delete "${ad.title}"? This cannot be undone.`)) return
    setBusyId(ad.ad_id)
    try {
      await api.delete(`/ads/admin/${ad.ad_id}`)
      setAds(prev => prev.filter(a => a.ad_id !== ad.ad_id))
      setTotalCount(c => c - 1)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete ad')
    } finally { setBusyId(null) }
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Ads</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Manage advertiser cards shown in the Browse feed.</div>
        </div>
        <button onClick={() => setModalAd(null)}
          style={{ height: 40, padding: '0 20px', borderRadius: 9, border: 'none', background: '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 2px 8px rgba(232,68,10,0.25)' }}>
          📣 New Ad
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Ads"        value={stats.total.toLocaleString()}                                   />
        <StatCard label="Active"           value={stats.active.toLocaleString()}             accent="#15803d"     />
        <StatCard label="Paused"           value={stats.paused.toLocaleString()}              accent="#b45309"     />
        <StatCard label="Total Impressions" value={stats.total_impressions.toLocaleString()}  accent="#1d4ed8"     />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '11px 16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search advertiser or title…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {search && <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>

        <button onClick={fetchAds} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{totalCount.toLocaleString()} ads</div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 1.6fr 0.9fr 1.2fr 0.9fr 1.6fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
          {['Ad','Advertiser','Status','Dates','Impressions',''].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading ads…</div>
        ) : ads.length === 0 ? (
          <div style={{ padding: '56px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📣</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>No ads yet</div>
            <div style={{ fontSize: 13, color: '#9a9a94', marginBottom: 20 }}>Create an ad to show it in the Browse feed.</div>
            <button onClick={() => setModalAd(null)}
              style={{ height: 40, padding: '0 20px', borderRadius: 8, border: 'none', background: '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              📣 New Ad
            </button>
          </div>
        ) : ads.map((ad, i) => {
          const s = STATUS[ad.status] || STATUS.active
          const busy = busyId === ad.ad_id
          return (
            <div key={ad.ad_id}
              style={{ display: 'grid', gridTemplateColumns: '2.2fr 1.6fr 0.9fr 1.2fr 0.9fr 1.6fr', padding: '13px 20px', borderBottom: i < ads.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <div style={{ width: 44, height: 44, borderRadius: 7, background: '#f3f3f0', flexShrink: 0, overflow: 'hidden' }}>
                  <img src={ad.image_url} alt={ad.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ad.title}</div>
                  {ad.budget_amount != null && <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{money(ad.budget_amount)}</div>}
                </div>
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ad.advertiser_name}</div>
                {ad.advertiser_contact && <div style={{ fontSize: 11, color: '#9a9a94', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ad.advertiser_contact}</div>}
              </div>

              <div>
                <span style={{ fontSize: 10, fontWeight: 800, color: s.color, background: s.bg, padding: '3px 8px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>{s.label}</span>
              </div>

              <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                {ad.start_date || ad.end_date ? `${fmt(ad.start_date)} – ${fmt(ad.end_date)}` : 'Always'}
              </div>

              <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>{Number(ad.impressions_count || 0).toLocaleString()}</div>

              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button onClick={() => setModalAd(ad)} disabled={busy}
                  style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', color: '#111317', fontSize: 11, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  Edit
                </button>
                {ad.status === 'active' && (
                  <button onClick={() => handleStatusChange(ad, 'paused')} disabled={busy}
                    style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: '#fffbeb', color: '#b45309', fontSize: 11, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    Pause
                  </button>
                )}
                {ad.status === 'paused' && (
                  <button onClick={() => handleStatusChange(ad, 'active')} disabled={busy}
                    style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    Resume
                  </button>
                )}
                {ad.status !== 'ended' && (
                  <button onClick={() => handleEnd(ad)} disabled={busy}
                    style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: '#f3f4f6', color: '#374151', fontSize: 11, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    End
                  </button>
                )}
                <button onClick={() => handleDelete(ad)} disabled={busy}
                  style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  Delete
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {modalAd !== undefined && (
        <AdModal ad={modalAd} onClose={() => setModalAd(undefined)} onSaved={handleSaved} />
      )}
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}
